import React from 'react';
import styles from './Tags.module.css';

/* ══════════════════════════════════════════════════════════════════
 * Badge — 상태 라벨
 * ════════════════════════════════════════════════════════════════ */

export type BadgeTone = 'brand' | 'weak' | 'lime' | 'neutral' | 'danger' | 'outline';

interface BadgeProps {
    tone?: BadgeTone;
    children: React.ReactNode;
    className?: string;
}

export const Badge = ({ tone = 'neutral', children, className }: BadgeProps) => (
    <span className={[styles.badge, styles[tone], className || ''].filter(Boolean).join(' ')}>
        {children}
    </span>
);

/* ══════════════════════════════════════════════════════════════════
 * TimeBadge — 브랜드색은 시간에 쓴다는 규칙의 구현체.
 *
 * 참고 앱은 같은 자리에 '개수'(후기 99)를 넣지만 모이는 '시간'을 넣습니다.
 * D-3 · 2시간 후 · 지금 출발 — 이 자리만 브랜드색을 씁니다.
 * ════════════════════════════════════════════════════════════════ */

interface TimeBadgeProps {
    /** 표시할 문구 — 호출부에서 i18n 으로 만들어 넘깁니다 */
    children: React.ReactNode;
    size?: 'inline' | 'sm' | 'md' | 'lg';
    /** 이미 지난 시간이면 강조를 뺍니다 */
    past?: boolean;
    /** 임박 표시 — 색만으로 구분하지 않도록 점을 함께 붙입니다 */
    urgent?: boolean;
}

export const TimeBadge = ({ children, size = 'md', past, urgent }: TimeBadgeProps) => {
    const sizeCls =
        size === 'inline' ? styles.timeInline
            : size === 'sm' ? styles.timeSm
                : size === 'lg' ? styles.timeLg
                    : styles.timeMd;
    return (
        <span className={[styles.time, sizeCls, past ? styles.timePast : ''].filter(Boolean).join(' ')}>
            {urgent && !past ? <i className={styles.dot} aria-hidden /> : null}
            {children}
        </span>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * Chip / ChipBar — 필터
 * ════════════════════════════════════════════════════════════════ */

interface ChipProps {
    label: string;
    selected?: boolean;
    disabled?: boolean;
    onSelect?: () => void;
    /** 좌측 아이콘 — 인라인 SVG */
    icon?: React.ReactNode;
}

export const Chip = ({ label, selected, disabled, onSelect, icon }: ChipProps) => (
    <button
        type="button"
        role="radio"
        aria-checked={!!selected}
        disabled={disabled}
        onClick={onSelect}
        className={[
            styles.chip,
            selected ? styles.chipOn : '',
            disabled ? styles.chipDisabled : '',
        ]
            .filter(Boolean)
            .join(' ')}
    >
        {icon}
        {label}
    </button>
);

export interface ChipOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
}

interface ChipBarProps {
    options: ChipOption[];
    value: string;
    onChange: (value: string) => void;
    /** 스크린리더용 그룹 이름 — i18n 으로 넘깁니다 */
    ariaLabel: string;
}

/** 가로 스크롤 칩 목록. 거터 밖까지 스크롤되지만 첫 칩은 거터에 맞춰 시작합니다. */
export const ChipBar = ({ options, value, onChange, ariaLabel }: ChipBarProps) => (
    <div className={styles.chipBar} role="radiogroup" aria-label={ariaLabel}>
        {options.map((o) => (
            <Chip
                key={o.value}
                label={o.label}
                icon={o.icon}
                disabled={o.disabled}
                selected={o.value === value}
                onSelect={() => onChange(o.value)}
            />
        ))}
    </div>
);

/* ══ 고르는 손잡이 ══ */

interface FilterChipProps {
    /** 아무것도 안 골랐을 때 보여줄 말 (예: 카테고리) */
    placeholder: string;
    /** 고른 값. 있으면 진하게 */
    value?: string;
    onClick: () => void;
}

/** 눌러서 바텀시트를 여는 칩. (여러 개를 한 줄에 둘 때는 Controls 의 FilterBar 안에 넣습니다)
 *  기준 위치·카테고리·정렬처럼 "지금 무엇으로 좁히고 있는지"를
 *  화면에 계속 띄워 두기 위한 것입니다. 값이 있으면 진하게 보입니다. */
export const FilterChip = ({ placeholder, value, onClick }: FilterChipProps) => (
    <button
        type="button"
        className={value ? `${styles.filterChip} ${styles.filterChipOn}` : styles.filterChip}
        onClick={onClick}
    >
        <span className={styles.filterChipText}>{value || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="6 9 12 15 18 9" />
        </svg>
    </button>
);
